import { zodResolver } from "@hookform/resolvers/zod"
import { Dialog, DialogActions, DialogContent, DialogTitle, Divider, IconButton, TextField } from "@mui/material"
import { CloseOutlined } from "@mui-symbols-material/w300"
import type { FunctionComponent } from "react"
import { FormProvider, useForm } from "react-hook-form"

import { useUpdateProduct } from "../hooks/useUpdateProduct"

import type { ProductSkuAddFormValues } from "./productFormSchema"
import { ProductSkuAddFormSchema } from "./productFormSchema"

import { Button } from "@/components/common/Button"
import { FormField } from "@/components/Forms"
import type { Product } from "@/graphql/codegen/graphql"

interface AddProductSkuDialogProps {
  product: Product
  onClose: () => void
  onSubmit: () => void
}

const AddProductSkuDialog: FunctionComponent<AddProductSkuDialogProps> = ({ product, onClose, onSubmit }) => {
  const methods = useForm<ProductSkuAddFormValues>({
    defaultValues: {
      vendorSku: "",
    },
    resolver: zodResolver(ProductSkuAddFormSchema),
    mode: "all",
  })
  const {
    formState: { isDirty, isValid, isSubmitting },
    handleSubmit,
  } = methods
  const canSubmit = isDirty && isValid && !isSubmitting
  const { update } = useUpdateProduct({ onSuccess: onSubmit })

  const submitForm = async (formValues: ProductSkuAddFormValues) => {
    await update({
      productId: product.productId,
      vendorSku: formValues.vendorSku,
    })
  }

  return (
    <Dialog open onClose={onClose} fullWidth>
      <DialogTitle className='pl-10 font-normal text-primary'>Add SKU</DialogTitle>
      <IconButton aria-label='close' onClick={onClose} className='absolute right-4 top-4 text-primary'>
        <CloseOutlined />
      </IconButton>
      <Divider />
      <DialogContent classes={{ root: "p-10" }}>
        <FormProvider {...methods}>
          <FormField
            name='vendorSku'
            label='Vendor SKU'
            render={({ field, fieldState: { error } }) => (
              <TextField {...field} error={!!error} helperText={error?.message} fullWidth placeholder='Enter SKU' />
            )}
          />
        </FormProvider>
      </DialogContent>
      <DialogActions className='justify-between px-10 pb-10'>
        <Button appearance='outlined' onClick={onClose}>
          Cancel
        </Button>
        <Button disabled={!canSubmit} onClick={handleSubmit(submitForm)}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default AddProductSkuDialog
